import { css } from '@emotion/react';
import { COLOR_PALETTE } from '@inflearn/ds-react';

const HAMMER_CURSOR_URL = `${process.env.NEXT_PUBLIC_CDN_URL}/public/games/hammer_128.png`;
const HAMMER_EFFECT_CURSOR_URL = `${process.env.NEXT_PUBLIC_CDN_URL}/public/games/hammer_effect_128.png`;

export const gameWrapperStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`;

export const gameBoardStyle = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 0 auto;
  padding: 16px;
  max-width: 480px;
  width: 480px;
  height: 480px;
  perspective: 1000px;
  cursor: url('${HAMMER_CURSOR_URL}') 16 16, auto;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  border-radius: 16px;
  border: 1px solid ${COLOR_PALETTE.gray[1]};
  background-color: #FFF;

  &:active {
    cursor: url('${HAMMER_EFFECT_CURSOR_URL}') 16 16, auto;
  }
`;

export const gameCellStyle = () => css`
  aspect-ratio: 1;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: visible;
`;

export const characterButtonStyle = (isShark = false) => css`
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  border-radius: 12px;
  cursor: url('${HAMMER_CURSOR_URL}') 16 16, pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  animation: ${isShark ? 'sharkPopIn' : 'popIn'} 0.2s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  padding: 0;
  position: relative;
  overflow: visible;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95) rotate(-20deg);
    cursor: url('${HAMMER_EFFECT_CURSOR_URL}') 16 16, pointer;
    animation: ${isShark ? 'sharkClickEffect' : 'clickEffect'} 0.3s ease-out;
  }

  @keyframes popIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    60% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes sharkPopIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    60% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes clickEffect {
    0% {
      transform: scale(1) rotate(0deg);
    }
    20% {
      transform: scale(1.1) rotate(-25deg);
    }
    40% {
      transform: scale(0.9) rotate(-20deg);
    }
    60% {
      transform: scale(0.8) rotate(-15deg);
      opacity: 0.7;
    }
    100% {
      transform: scale(0) rotate(0deg);
      opacity: 0;
    }
  }

  @keyframes sharkClickEffect {
    0% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
    20% {
      transform: scale(1.1) rotate(-25deg);
    }
    40% {
      transform: scale(0.9) rotate(-20deg);
    }
    60% {
      transform: scale(0.8) rotate(-15deg);
      opacity: 0.7;
    }
    100% {
      transform: scale(0) rotate(0deg);
      opacity: 0;
    }
  }
`;

export const characterEmojiStyle = (isShark = false) => css`
  font-size: ${isShark ? '48px' : '16px'};
  font-weight: ${isShark ? 'normal' : '800'};
  line-height: ${isShark ? '1' : '1.3'};
  color: ${isShark ? 'inherit' : '#222'};
  font-family: ${isShark ? 'inherit' : "'Monaco', 'Menlo', 'Consolas', monospace"};
  text-align: center;
  letter-spacing: ${isShark ? 'normal' : '-0.3px'};
  word-break: break-word;
  max-width: 95%;
`;

export const scoreTagStyle = (isShark = false) => css`
  font-size: 11px;
  font-weight: bold;
  color: ${isShark ? '#0288D1' : '#4CAF50'};
  background: rgba(255, 255, 255, 0.95);
  padding: 2px 6px;
  border-radius: 6px;
  ${isShark && 'border: 1px solid #80D4FF;'}
`;

export const scorePopupStyle = (isShark: boolean) => css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  animation: ${isShark ? 'sharkShake' : 'scoreFloat'} 1s ease-out forwards;

  @keyframes scoreFloat {
    0% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(0.5);
    }
    30% {
      transform: translate(-50%, -70px) scale(1.3);
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -100px) scale(1);
    }
  }

  @keyframes sharkShake {
    0% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    10% {
      transform: translate(-55%, -50%) scale(1.2);
    }
    20% {
      transform: translate(-45%, -50%) scale(1.2);
    }
    30% {
      transform: translate(-55%, -50%) scale(1.2);
    }
    40% {
      transform: translate(-50%, -50%) scale(1.2);
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -80px) scale(0.8);
    }
  }
`;

export const scoreTextStyle = (isShark: boolean) => css`
  font-size: ${isShark ? '32px' : '48px'};
  font-weight: bold;
  color: ${isShark ? 'rgb(255, 52, 72)' : '#4CAF50'};
  z-index: 2;
  position: relative;
`;

export const sharkMessageStyle = css`
  font-size: 16px;
  font-weight: 700;
  color:rgb(255, 52, 72);
  z-index: 1;
  white-space: nowrap;
`;

export const characterImageStyle = css`
  width: 100%;
  height: auto;
  object-fit: contain;
  transition: transform 0.2s ease;
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
`;

export const topImageContainerStyle = css`
  position: fixed;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 20px;
`;

export const topImageStyle = css`
  width: 220px;
  height: auto;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
`;
